/**
 * Custom Predictive Search - Sneakers Depot
 * Full-width horizontal product display
 * Smart positioning below entire header
 */

class CustomPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector("[data-search-results]");
    this.searchTimer = null;
    this.abortController = null;
  }

  connectedCallback() {
    if (!this.input || !this.resultsContainer) return;

    this.updateHeaderHeight();
    window.addEventListener("resize", () => this.updateHeaderHeight());

    let ticking = false;
    window.addEventListener("scroll", () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateHeaderHeight();
          ticking = false;
        });
        ticking = true;
      }
    });

    this.input.addEventListener("input", this.handleInput.bind(this));
    this.input.addEventListener("focus", this.handleFocus.bind(this));

    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.close();
      }
    });
  }

  updateHeaderHeight() {
    const menuItems = document.querySelectorAll(
      ".header__inline-menu .header__menu-item"
    );

    if (menuItems.length > 0) {
      let maxBottom = 0;

      menuItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom > maxBottom) {
          maxBottom = rect.bottom;
        }
      });

      document.documentElement.style.setProperty(
        "--header-total-height",
        `${maxBottom}px`
      );
    } else {
      const menuWrapper = document.querySelector(".header__menu-wrapper");
      if (menuWrapper) {
        const rect = menuWrapper.getBoundingClientRect();
        document.documentElement.style.setProperty(
          "--header-total-height",
          `${rect.bottom}px`
        );
      }
    }
  }

  handleInput(event) {
    const query = event.target.value.trim();

    clearTimeout(this.searchTimer);

    if (query.length < 2) {
      this.close();
      return;
    }

    this.searchTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 300);
  }

  handleFocus(event) {
    const query = event.target.value.trim();
    if (query.length >= 2) {
      this.fetchResults(query);
    }
  }

  async fetchResults(query) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.showLoading();

    try {
      const response = await fetch(
        `${
          window.Shopify.routes.root
        }search/suggest.json?q=${encodeURIComponent(
          query
        )}&resources[type]=product&resources[limit]=5`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) throw new Error("Search failed");

      const data = await response.json();
      this.renderResults(data.resources.results.products || [], query);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search error:", error);
        this.hideLoading();
      }
    }
  }

  renderResults(products, query) {
    this.hideLoading();

    const noResultsLabel =
      window.theme?.strings?.no_results || "Brak wyników dla";

    if (products.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="custom-search-no-results">
          <p>${noResultsLabel}: "${query}"</p>
        </div>
      `;
      this.open();
      return;
    }

    const productsHTML = products
      .map(
        (product) => `
      <div class="custom-search-product-card">
        <a href="${product.url}" class="custom-search-product-link">
          <div class="custom-search-product-image">
            ${
              product.image
                ? `<img src="${product.image}" alt="${this.escapeHtml(
                    product.title
                  )}" width="252" height="252" loading="lazy">`
                : '<div class="custom-search-placeholder">No Image</div>'
            }
          </div>
          <div class="custom-search-product-info">
            <h3 class="custom-search-product-title">${this.escapeHtml(
              product.title
            )}</h3>
            ${
              product.vendor
                ? `<p class="custom-search-product-vendor">${this.escapeHtml(
                    product.vendor
                  )}</p>`
                : ""
            }
            <p class="custom-search-product-price">${this.formatMoney(
              product.price
            )}</p>
          </div>
        </a>
      </div>
    `
      )
      .join("");

    const resultsTitle =
      window.theme?.strings?.search_results_title ||
      "Wyniki dla twojego wyszukiwania:";
    const viewAllLabel = window.theme?.strings?.view_all || "Zobacz wszystko";

    this.resultsContainer.innerHTML = `
      <div class="custom-search-results-header">
        <h2 class="custom-search-results-title">${resultsTitle}</h2>
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(
      query
    )}" class="custom-search-view-all">
          ${viewAllLabel}
        </a>
      </div>
      <div class="custom-search-products-grid">
        ${productsHTML}
      </div>
    `;

    this.open();
  }

  showLoading() {
    this.resultsContainer.innerHTML = `
      <div class="custom-search-loading">
        <svg class="custom-search-spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
    this.open();
  }

  hideLoading() {
    const loading = this.querySelector(".custom-search-loading");
    if (loading) loading.remove();
  }

  open() {
    this.updateHeaderHeight();
    this.resultsContainer.style.display = "block";
    this.setAttribute("open", "");
  }

  close() {
    this.resultsContainer.style.display = "none";
    this.removeAttribute("open");
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  formatMoney(price) {
    const cents = Math.round(parseFloat(price) * 100);
    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      "{{amount}} PLN";
    const amount = cents / 100;

    let value = "";
    const formatString =
      moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || "amount";

    switch (formatString) {
      case "amount":
        value = amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_no_decimals":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        break;
      case "amount_with_comma_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        break;
      case "amount_with_space_separator":
        value = amount
          .toFixed(2)
          .replace(".", ",")
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      case "amount_no_decimals_with_space_separator":
        value = Math.round(amount)
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
        break;
      default:
        value = amount.toFixed(2);
    }

    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
}

customElements.define("custom-predictive-search", CustomPredictiveSearch);
